import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import {
  EmptyEntityWithTicketsQuery,
  QueryWithViewMode,
} from 'src/utils/eventQueryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CatalogResults,
  EntityWithTicketsQuery,
  EventWithData,
  Listing,
  PurchaseOrderTicketGroup,
  Sale,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContext } from '../CatalogDataContext';
import { useFilterQueryContext } from '../FilterQueryContext';
import {
  SearchDataContext,
  SearchDataContextProviderProps,
} from './SearchDataContext.types';
import {
  buildCatalogAndEventsFromListingsSearchData,
  buildCatalogAndEventsFromSalesSearchData,
} from './SearchDataContext.utils';

export const useSearchDataContext = () => useContext(SearchDataContext);

/**
 * Context provider to retrieve search data from the server, and provide a similar interface to the catalog data context when consumed by table components.
 *
 * The search data context requests search data from the server which contains catalog data and entity data. This is later transformed into catalog data and entity data.
 * The context optionally provides a fallback to the catalog data context when the catalog data from search data is not available.
 */
export function SearchDataContextProvider<
  TQuery extends EntityWithTicketsQuery & QueryWithViewMode,
>({
  queryKey,
  entityType,
  getSearchData,
  getSearchDataSummary,
  children,
  disabled,
}: SearchDataContextProviderProps<TQuery>) {
  const { showErrorDialog } = useClientLogContext();
  const { filterQuery, isQueryInitialized } = useFilterQueryContext<TQuery>();
  const { activeAccountWebClientConfig } = useAppContext();

  const { data: catalogDataFallback } = useCatalogDataContext();

  const [errorInfo, setErrorInfo] = useState<{
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
  }>();

  const {
    searchText,
    viewMode, // this is not used for server and keeping it in this cause refresh to happen 2x
    ...serverSideFilterQuery // Don't use it because it's a new object on every render
  } = filterQuery;

  const searchDataFilterQuery = useMemo(() => {
    const query = Object.fromEntries(
      Object.entries(serverSideFilterQuery).filter(([_, v]) => v != null)
    );
    return {
      ...query,
      timezoneOffsetMins:
        query.timezoneOffsetMins ??
        EmptyEntityWithTicketsQuery.timezoneOffsetMins,
    } as TQuery;
  }, [serverSideFilterQuery]);

  const searchDataSummaryFilterQuery = useMemo(() => {
    return {
      ...searchDataFilterQuery,
      searchOptions: null,
    } as TQuery;
  }, [searchDataFilterQuery]);

  const shouldQuerySearchData = useMemo(
    () =>
      activeAccountWebClientConfig.activeAccountId != null &&
      searchDataFilterQuery != null &&
      isQueryInitialized &&
      !disabled,
    [
      activeAccountWebClientConfig.activeAccountId,
      searchDataFilterQuery,
      isQueryInitialized,
      disabled,
    ]
  );

  const searchDataQueryKey = useMemo(
    () => [queryKey + 'Expanded', searchDataFilterQuery, shouldQuerySearchData],
    [queryKey, searchDataFilterQuery, shouldQuerySearchData]
  );

  const searchDataSummaryQueryKey = useMemo(
    () => [
      queryKey + 'Summary',
      searchDataSummaryFilterQuery,
      shouldQuerySearchData,
    ],
    [queryKey, searchDataSummaryFilterQuery, shouldQuerySearchData]
  );

  const searchDataQuery = useQuery({
    queryKey: [searchDataQueryKey],
    queryFn: async () => {
      if (!shouldQuerySearchData) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const data = await getSearchData?.(
            searchDataFilterQuery.eventOrMappingIds ?? [],
            searchDataFilterQuery as TQuery
          );

          return data;
        },
        (error: ErrorTypes) => {
          showErrorDialog('getSearchData', error, {
            trackErrorData: {
              eventIds: searchDataFilterQuery.eventOrMappingIds ?? [],
              filterQuery,
            },
          });
        }
      );
    },
    enabled: shouldQuerySearchData,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false,
  });

  const searchDataSummaryQuery = useQuery({
    queryKey: [searchDataSummaryQueryKey],
    queryFn: async () => {
      if (!shouldQuerySearchData) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const data = await getSearchDataSummary?.(
            searchDataSummaryFilterQuery.eventOrMappingIds ?? [],
            searchDataSummaryFilterQuery as TQuery
          );

          return data;
        },
        (error: ErrorTypes) => {
          showErrorDialog('getSearchDataSummary', error, {
            trackErrorData: {
              eventIds: searchDataSummaryFilterQuery.eventOrMappingIds ?? [],
              filterQuery,
            },
          });
        }
      );
    },
    enabled: shouldQuerySearchData,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false,
  });

  // Local overrides for items that have been updated in the UI before the next refetch
  // Key is the item ID, value is the partial item data to merge
  const [localOverrides, setLocalOverrides] = useState<
    Record<string, Partial<Listing | Sale | PurchaseOrderTicketGroup>>
  >({});

  // Clear local overrides when search data changes (new search or refetch)
  useEffect(() => {
    if (searchDataQuery.data) {
      setLocalOverrides({});
    }
  }, [searchDataQuery.data]);

  // Handle query errors
  useEffect(() => {
    const failureReason =
      shouldQuerySearchData && searchDataQuery.failureReason;

    if (failureReason) {
      const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
        null,
        failureReason?.toString()
      );
      setErrorInfo({
        errorHeader: headerDisplay,
        errorMessage: messageDisplay,
      });
    } else if (errorInfo) {
      setErrorInfo(undefined);
    }
  }, [errorInfo, searchDataQuery.failureReason, shouldQuerySearchData]);

  // Update an item in the event data (used for optimistic UI updates)
  const updateItemInEvent = useCallback(
    (
      item: Listing | Sale | PurchaseOrderTicketGroup,
      entityType: ActionOutboxEntityType,
      _sectionalUpdate?: boolean
    ) => {
      // Determine the item ID based on entity type
      const itemId =
        entityType === ActionOutboxEntityType.Purchase
          ? (item as PurchaseOrderTicketGroup).tgId
          : item.id;

      if (!itemId) {
        return;
      }

      // Store the override for this item
      setLocalOverrides((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          ...item,
        },
      }));
    },
    []
  );

  const { catalogResults, eventsTransformed } = useMemo(() => {
    if (searchDataQuery.data == null) {
      return {
        catalogResults: {} as CatalogResults,
        eventsTransformed: [] as EventWithData[],
      };
    }

    const applyOverridesToEvents = (events: EventWithData[]): EventWithData[] => {
      if (Object.keys(localOverrides).length === 0) return events;

      return events.map((eventData) => {
        const newEventData = { ...eventData };

        if (entityType === ActionOutboxEntityType.Listing && newEventData.entities.listings) {
          newEventData.entities = {
            ...newEventData.entities,
            listings: newEventData.entities.listings.map((listing) => {
              const override = localOverrides[listing.id] as Partial<Listing> | undefined;
              return override ? ({ ...listing, ...override } as Listing) : listing;
            }),
          };
        } else if (entityType === ActionOutboxEntityType.Sale && newEventData.entities.sales) {
          newEventData.entities = {
            ...newEventData.entities,
            sales: newEventData.entities.sales.map((sale) => {
              const override = localOverrides[sale.id] as Partial<Sale> | undefined;
              return override ? ({ ...sale, ...override } as Sale) : sale;
            }),
          };
        }

        return newEventData;
      });
    };

    if (entityType === ActionOutboxEntityType.Sale) {
      const result = buildCatalogAndEventsFromSalesSearchData(
        searchDataQuery.data,
        searchDataSummaryQuery.data,
        catalogDataFallback
      );

      return {
        ...result,
        eventsTransformed: applyOverridesToEvents(result.eventsTransformed),
      };
    }

    if (entityType === ActionOutboxEntityType.Listing) {
      const result = buildCatalogAndEventsFromListingsSearchData(
        searchDataQuery.data,
        searchDataSummaryQuery.data,
        catalogDataFallback
      );

      return {
        ...result,
        eventsTransformed: applyOverridesToEvents(result.eventsTransformed),
      };
    }

    return {
      catalogResults: {} as CatalogResults,
      eventsTransformed: [] as EventWithData[],
    };
  }, [
    searchDataQuery.data,
    entityType,
    searchDataSummaryQuery.data,
    catalogDataFallback,
    localOverrides,
  ]);

  return (
    <SearchDataContext.Provider
      value={{
        isEnabled: !disabled,
        isLoading: searchDataQuery.isFetching,
        data: catalogResults ?? undefined,
        indexLastUpdatedAt: searchDataQuery.data?.indexLastUpdatedAt,
        pitId: searchDataQuery.data?.pitId,
        totalItemsCount: searchDataQuery.data?.totalItemsCount,
        searchAfter: searchDataQuery.data?.searchAfter,
        errorInfo,
        eventsTransformed,
        refetch: () => {
          searchDataQuery.refetch();
          searchDataSummaryQuery.refetch();
        },
        updateItemInEvent,
      }}
    >
      {children}
    </SearchDataContext.Provider>
  );
}
